import { useCallback, useEffect, useState } from 'react';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../utils/safeStorage';
import { useZoomPreview } from './useZoomPreview';

export const DASHBOARD_IMAGE_SIZE_MIN = 34;
export const DASHBOARD_IMAGE_SIZE_MAX = 96;
export const DASHBOARD_IMAGE_SIZE_DEFAULT = 42;

function clampImageSize(value: number, fallback = DASHBOARD_IMAGE_SIZE_DEFAULT): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(DASHBOARD_IMAGE_SIZE_MAX, Math.max(DASHBOARD_IMAGE_SIZE_MIN, Math.round(value)));
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function useDashboardImageSettings(
  storageKey: string,
  defaultSize = DASHBOARD_IMAGE_SIZE_DEFAULT
) {
  const [cropSize, setCropSizeState] = useState(() => {
    const stored = safeLocalStorageGetItem(storageKey);
    return stored === null
      ? clampImageSize(defaultSize)
      : clampImageSize(Number(stored), defaultSize);
  });
  const zoom = useZoomPreview();
  const { toggleZoom } = zoom;

  const setCropSize = useCallback(
    (value: number) => {
      const nextValue = clampImageSize(value, defaultSize);
      setCropSizeState(nextValue);
      safeLocalStorageSetItem(storageKey, String(nextValue));
    },
    [defaultSize, storageKey]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'z') return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      toggleZoom();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleZoom]);

  return {
    cropSize,
    setCropSize,
    ...zoom,
  };
}
